import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';

function copyText(text: string) {
  void navigator.clipboard.writeText(text);
  toast.success('Copied');
}

function CodeBlock({ children }: { children: string }) {
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed">
        {children}
      </pre>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-1.5 top-1.5 h-6 w-6"
        aria-label="Copy code"
        onClick={() => copyText(children)}
      >
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/**
 * In-app "How to install" guide for the booking widget — the counterpart to the
 * web-chat install dialog, so both embeddable widgets are documented in the
 * product rather than only in the repo.
 */
export function BookingInstallHelpDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Install online booking on your website</DialogTitle>
          <DialogDescription>
            Let visitors book straight from your site. The widget opens your booking page in an
            overlay, so customers get the full flow — service, address, pricing with tax, real
            availability — and you get a real job on your schedule.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          <section className="space-y-2">
            <h3 className="font-semibold">1. Create a link &amp; copy the snippet</h3>
            <p className="text-muted-foreground">
              Add the services you want to offer on the <strong>Services</strong> tab, then create a
              link here choosing which of them it exposes. Click <strong>Embed code</strong> on the
              link to copy your snippet — it looks like this, with your own booking key:
            </p>
            <CodeBlock>{`<!-- Notifi Online Booking -->
<script>
  window.notifiBookingSettings = { bookingKey: "your_booking_key" };
</script>
<script async src="https://YOUR_NOTIFI_API_HOST/api/public/booking-widget/loader.js"></script>`}</CodeBlock>
            <p className="text-muted-foreground">
              Paste it immediately before the closing <code>&lt;/body&gt;</code> tag on every page
              you want it on. A <strong>Book now</strong> button appears in the bottom-right — no
              visitor login needed.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="font-semibold">2. By platform</h3>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>
                <strong>Plain HTML / static site:</strong> paste the snippet before{' '}
                <code>&lt;/body&gt;</code>.
              </li>
              <li>
                <strong>WordPress:</strong> add it to the theme footer, or use a “header &amp;
                footer scripts” plugin.
              </li>
              <li>
                <strong>Webflow / Squarespace / Wix:</strong> add it as custom footer / embed code
                in your site settings.
              </li>
              <li>
                <strong>Shopify:</strong> add it to <code>theme.liquid</code> before{' '}
                <code>&lt;/body&gt;</code>.
              </li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="font-semibold">3. Use your own button (optional)</h3>
            <p className="text-muted-foreground">
              If your site already has a “Book a visit” button, add <code>mode: "manual"</code> to
              hide the floating one:
            </p>
            <CodeBlock>{`<script>
  window.notifiBookingSettings = {
    bookingKey: "your_booking_key",
    mode: "manual",
  };
</script>`}</CodeBlock>
            <p className="text-muted-foreground">
              Then mark any element with <code>data-notifi-booking</code>, or open it yourself:
            </p>
            <CodeBlock>{`<button data-notifi-booking>Book a visit</button>

<!-- or, from your own code -->
<button onclick="window.NotifiBooking('open')">Book a visit</button>`}</CodeBlock>
            <CodeBlock>{`window.NotifiBooking('open');      // open the booker
window.NotifiBooking('close');     // close it
window.NotifiBooking('shutdown');  // remove the widget from the page`}</CodeBlock>
          </section>

          <section className="space-y-2">
            <h3 className="font-semibold">4. Show real prices (recommended)</h3>
            <p className="text-muted-foreground">
              On the <strong>Services</strong> tab, attach pricebook items to a service under{' '}
              <strong>Quoted pricebook lines</strong>. Customers then see an itemised summary with{' '}
              <strong>tax calculated for their address</strong> before they confirm — and the job
              lands on your schedule with those exact lines, so what they agreed to is what you
              invoice.
            </p>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Mark a line “Optional” to offer it as an add-on the customer can tick.</li>
              <li>
                At least one line must be required — that is the service they are actually booking.
              </li>
              <li>
                Leave the lines empty to show a single flat price instead, or turn off{' '}
                <strong>Show prices</strong> in Appearance to show “Quoted”.
              </li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="font-semibold">5. Match your brand (optional)</h3>
            <p className="text-muted-foreground">
              Edit the link and open <strong>Appearance</strong> to set the theme colour, headline,
              welcome message, button label and which corner the launcher sits in. The theme colour
              carries through the whole booking flow, so it looks like part of your site.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="font-semibold">6. Track completed bookings (optional)</h3>
            <p className="text-muted-foreground">
              Fire your own analytics when a booking completes:
            </p>
            <CodeBlock>{`<script>
  window.notifiBookingSettings = {
    bookingKey: "your_booking_key",
    onBookingCompleted: function (e) {
      // e.referenceNumber, e.serviceName
      // e.g. gtag('event', 'booking_completed');
    },
  };
</script>`}</CodeBlock>
          </section>

          <section className="space-y-2">
            <h3 className="font-semibold">Using it alongside web chat</h3>
            <p className="text-muted-foreground">
              Both widgets can live on the same page with no extra setup. If you turn on{' '}
              <strong>Book via widget</strong> for a chat channel (Settings → Channels), your AI
              receptionist stops booking inside the conversation and instead offers a{' '}
              <strong>Book now</strong> button that opens this booker — so the customer gets the
              itemised pricing the chat cannot show.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="font-semibold">What happens on your side</h3>
            <p className="text-muted-foreground">
              Every booking creates a real contact, job and appointment — the same records your
              office would create by hand, on the same schedule board, with the quoted line items
              and tax already attached. If the service takes a deposit, the customer gets a payment
              link on the confirmation screen.
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
